import { cn } from "@tellescope/design-system";
import { BlockProps } from "../../../../types";
import { BlockHeader } from "../../BlockHeader";
import { BlockContent } from "../../BlockContent";
import { BlockEmptyState } from "../../BlockEmptyState";
import { FieldCell } from "./FieldCell";
import { FieldsFilterPopover } from "./FieldsFilterPopover";
import { useFieldsBlock } from "../../../../hooks";

export const FieldsBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  ...rest
}: BlockProps) => {
  const {
    t,
    parsedInfo,
    fields,
    filterBlock,
    filterFieldOptions,
    fieldNameOptions,
    onFilterChange,
    currentUserId,
    editingKey,
    editingError,
    startEditing,
    stopEditing,
    saveField,
    saveCustomField,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canEditBlock,
    canCreateBlock,
    canDeleteBlock,
    canEditEnduser,
  } = useFieldsBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  const canEdit = canEditEnduser && editMode;

  return (
    <div className={cn("group @container", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          canEditBlock && (
            <FieldsFilterPopover
              filterBlock={filterBlock}
              fieldOptions={filterFieldOptions}
              nameOptions={fieldNameOptions}
              onFilterChange={onFilterChange}
            />
          )
        }
      />
      <BlockContent
        editMode={editMode}
        setEditMode={canEditEnduser ? setEditMode : undefined}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("grid grid-cols-1 @sm:grid-cols-2 gap-4", contentClassName)}
      >
        {fields.length > 0 ? (
          fields.map((field) => (
            <FieldCell
              key={field.key}
              field={field}
              editing={editingKey === field.key}
              canEdit={canEdit}
              error={editingKey === field.key ? editingError : undefined}
              currentUserId={currentUserId}
              onStartEditing={startEditing}
              onStopEditing={stopEditing}
              onSaveField={saveField}
              onSaveCustomField={saveCustomField}
            />
          ))
        ) : (
          <BlockEmptyState className="col-span-full">{t("No matching fields")}</BlockEmptyState>
        )}
      </BlockContent>
    </div>
  );
};
